import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { base_path } from "./environment";
import "../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "../src/style/css/feather.css";
import "../src/index.scss";
import store from "./core/data/redux/store";
import { Provider } from "react-redux";
import "../src/style/icon/boxicons/boxicons/css/boxicons.min.css";
import "../src/style/icon/weather/weathericons.css";
import "../src/style/icon/typicons/typicons.css";
import "../node_modules/@fortawesome/fontawesome-free/css/fontawesome.min.css";
import "../node_modules/@fortawesome/fontawesome-free/css/all.min.css";
import "../src/style/icon/ionic/ionicons.css";
import "../src/style/icon/tabler-icons/webfont/tabler-icons.css";
import ALLRoutes from "./feature-module/router/router";
import "../node_modules/bootstrap/dist/js/bootstrap.bundle.min.js";
import { Brand, BrandProvider } from "./contexts/BrandContext";
import { instalarOperadorNoFetch } from "./core/operadorFetch";

// Antes de qualquer tela montar: toda chamada à API passa a dizer QUEM está logado (log de auditoria).
instalarOperadorNoFetch();

// White label multi-tenant: branding resolvido pelo backend a partir do Host.
// Endpoint relativo `/api/branding` — em produção o nginx strippa `/api/`;
// em dev a chave "proxy" do package.json proxia para a API de produção.
const BRANDING_URL = "/api/branding";
const BRAND_CACHE_KEY = "__brand_cache_v1";
const BRAND_CACHE_TTL = 60 * 60 * 1000; // 1h

// Fallback usado quando o backend não responde. Usa logoconversa.png
// (logo vivo da ConversAI) para não regredir o visual da marca padrão.
const DEFAULT_BRAND: Brand = {
  host: "fallback",
  nome_produto: "ConversAI CRM",
  nome_curto: "ConversAI",
  cor_primaria: "#007e94",
  cor_secundaria: null,
  theme_color: "#007e94",
  logo_url: "/assets/img/logoconversa.png",
  logo_white_url: "/assets/img/logo-white.svg",
  logo_small_url: "/assets/img/logo-small.svg",
  favicon_url: "/assets/img/favicon.png",
  apple_touch_url: "/assets/img/apple-touch-icon.png",
  meta_description: "ConversAI CRM · Plataforma de atendimento inteligente",
  meta_keywords: "crm, atendimento, whatsapp, vendas, pipeline, conversai",
  lang: "pt-BR",
  suporte_email: "suporte@conversaicrm.com.br",
  suporte_whatsapp: null,
};

async function fetchBrand(): Promise<Brand> {
  const r = await fetch(BRANDING_URL, { cache: "no-store" });
  if (!r.ok) throw new Error("branding " + r.status);
  return (await r.json()) as Brand;
}

async function loadBrand(): Promise<Brand> {
  // 1. cache (render instantâneo no segundo acesso) + revalidação em background
  const cached = localStorage.getItem(BRAND_CACHE_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.brand && Date.now() - parsed.ts < BRAND_CACHE_TTL) {
        fetchBrand()
          .then((fresh) => {
            localStorage.setItem(
              BRAND_CACHE_KEY,
              JSON.stringify({ brand: fresh, ts: Date.now() })
            );
          })
          .catch(() => {});
        return parsed.brand as Brand;
      }
    } catch (e) {
      /* cache corrompido, segue para fetch fresco */
    }
  }

  // 2. fetch fresco
  try {
    const brand = await fetchBrand();
    localStorage.setItem(
      BRAND_CACHE_KEY,
      JSON.stringify({ brand, ts: Date.now() })
    );
    return brand;
  } catch (e) {
    console.warn("Branding fetch falhou, usando default:", e);
    return DEFAULT_BRAND;
  }
}

// "#007e94" -> "0, 126, 148". Aceita #rgb e #rrggbb. null se inválido.
function hexToRgb(hex: string): string | null {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const num = parseInt(h, 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
}

function applyBrandToHead(brand: Brand) {
  document.title = brand.nome_produto;
  document.documentElement.lang = brand.lang || "pt-BR";

  document
    .querySelector('meta[name="description"]')
    ?.setAttribute("content", brand.meta_description || "");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", brand.theme_color || "#007e94");
  document
    .querySelector('meta[name="keywords"]')
    ?.setAttribute("content", brand.meta_keywords || "");

  const icon =
    document.querySelector("link[rel='shortcut icon']") ||
    document.querySelector("link[rel='icon']");
  if (icon) icon.setAttribute("href", brand.favicon_url);

  const apple = document.querySelector("link[rel='apple-touch-icon']");
  if (apple) {
    apple.setAttribute("href", brand.apple_touch_url || brand.favicon_url);
  }

  document.documentElement.style.setProperty(
    "--brand-primary",
    brand.cor_primaria
  );
  // --brand-primary-rgb: componentes r,g,b (sem alpha) para uso em rgba(...).
  // Permite que fundos/realces "soft" (rgba teal hardcoded) sigam a marca.
  const rgb = hexToRgb(brand.cor_primaria);
  if (rgb) {
    document.documentElement.style.setProperty("--brand-primary-rgb", rgb);
  }
  if (brand.cor_secundaria) {
    document.documentElement.style.setProperty(
      "--brand-secondary",
      brand.cor_secundaria
    );
  }
}

(async () => {
  const brand = await loadBrand();
  applyBrandToHead(brand);
  (window as any).__BRAND__ = brand; // debug

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <BrandProvider value={brand}>
        <Provider store={store}>
          <BrowserRouter basename={base_path}>
            <ALLRoutes />
          </BrowserRouter>
        </Provider>
      </BrandProvider>
    </React.StrictMode>
  );
})();
